import React, { useEffect } from "react";
import { connect } from "react-redux";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { routerActions } from "connected-react-router";
import { useLocation, useParams } from "react-router-dom";
import { ResponsiveHeader } from "pages/_components/header";
import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import CredentialForm from "pages/_components/Forms/CredentialForm";
import { actions as depositsActions, selectors as depositsSelectors } from "reducers/deposits";
import gaEventTracker, { events } from "util/gaEventTracker";
import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import * as i18n from "util/i18n";
import FormattedDate from "pages/_components/FormattedDate";
import { formatNum } from "util/format";

const DepositCancelRenewal = ({
    dispatch,
    fetching,
    selectedDeposit,
    depositRequiredCredentials,
    depositTicketStatus,
    depositTicketId,
    depositsLength,
    isDesktop,
}) => {
    const { id } = useParams();
    const isLoading = fetching || !selectedDeposit?.idProduct || !depositRequiredCredentials?.length;
    const { state = {} } = useLocation();
    const { idTransaction = "" } = state;

    const credentialType = depositRequiredCredentials?.includes("otp") ? "otp" : "password";
    const depositData = {
        depositType: `${selectedDeposit?.productType}-${selectedDeposit?.currency}`,
        investCapital: selectedDeposit?.amountInvested || 0,
        debitAccount: {
            number: selectedDeposit?.accreditedAccount?.number,
            productType: selectedDeposit?.accreditedAccount?.productType,
        },
        termInDays: selectedDeposit?.deadline,

        currency: selectedDeposit?.currency,
        earnedInterests: selectedDeposit?.interest,
        chargeAmount: selectedDeposit?.amountTransfered,
        tna: selectedDeposit?.rate,
        expirationDate: selectedDeposit?.dueDate,
        autoRenewalLabel: selectedDeposit?.autoRenewalLabel,
        depositId: selectedDeposit?.idProduct,
    };

    useEffect(() => {
        if (depositsLength === 0) {
            dispatch(depositsActions.listDeposits());
        }
        dispatch(depositsActions.fetchCredentialGroup());
    }, [dispatch]);

    useEffect(() => {
        dispatch(depositsActions.readDeposit(id));
    }, [id, dispatch, depositsLength]);

    useEffect(() => {
        if (depositTicketStatus === "FORBIDEN") {
            dispatch(routerActions.push(`/deposits/cancelRenewal/forbiden/${id}`));
        }
        if (depositTicketStatus === "PENDING" && depositTicketId) {
            dispatch(routerActions.push(`/deposits/cancelRenewal/pendingTransaction/${depositTicketId}`));
        }
        if (depositTicketStatus === "FINISHED" && depositTicketId) {
            dispatch(routerActions.push(`/deposits/cancelRenewal/successTransaction/${depositTicketId}`));
        }
    }, [depositTicketStatus, depositTicketId]);

    const handleSubmit = ({ credential }, formikBag) => {
        if (idTransaction) {
            gaEventTracker.triggerEvent(events.pf_cancelar_renovacion_auto_firmar());
            dispatch(
                depositsActions.cancelAutoRenewal(
                    {
                        idTransaction,
                        [`_${credentialType}`]: credential,
                    },
                    formikBag,
                    credentialType,
                ),
            );
        } else {
            gaEventTracker.triggerEvent(events.pf_cancelar_renovacion_auto({ id_plazo_fijo: depositTicketId }));
            dispatch(
                depositsActions.cancelAutoRenewal(
                    {
                        ...depositData,
                        [`_${credentialType}`]: credential,
                    },
                    formikBag,
                    credentialType,
                ),
            );
        }
    };

    const handleBack = () => {
        let link;
        if (idTransaction) {
            link = "";
        } else {
            link = `/deposits/${id}`;
        }
        return link;
    };

    const currencyString = i18n.get(`currency.label.${depositData.currency}`);
    const debitAccount = `${i18n.get(`deposits.operative.account`, "")} ····${depositData?.debitAccount?.number?.slice(
        -4,
    )} ${depositData?.debitAccount?.productType?.slice(0, 2)} ${currencyString}`;

    return (
        <>
            <Notification scopeToShow="/deposits/details/cancelRenewal" />
            <ResponsiveHeader id="deposits.cancelAutoRenewal" backLinkTo={handleBack()} />
            <MainContainer showLoader={isLoading}>
                <div className="container">
                    <h2 className="text-lead" style={{ fontWeight: 500 }}>
                        {i18n.get("account.agreement.title")}
                    </h2>
                    <GridTable className="deposit-summary form-summary">
                        <GridTable.Container columns={isDesktop ? 3 : 1} rows={isDesktop ? 3 : 9}>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.type"
                                    titleTextType="I18N"
                                    titleClass="text-Primary-primary-color"
                                    valueText={selectedDeposit?.shortLabel}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.debitAccount"
                                    titleTextType="I18N"
                                    valueText={debitAccount}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.investCapital"
                                    titleTextType="I18N"
                                    valueText={`${currencyString} ${formatNum(depositData.investCapital, 2)}`}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.earnedInterests"
                                    titleTextType="I18N"
                                    valueText={`${currencyString} ${formatNum(depositData.earnedInterests, 2)}`}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.chargeAmount"
                                    titleTextType="I18N"
                                    valueText={`${currencyString} ${formatNum(depositData.chargeAmount, 2)}`}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.expirationDate"
                                    titleTextType="I18N"
                                    valueText={
                                        <FormattedDate date={depositData.expirationDate} dateFormat="DD/MM/YYYY" />
                                    }
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.termInDays"
                                    titleTextType="I18N"
                                    valueText={depositData.termInDays}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.tnaDetail"
                                    titleTextType="I18N"
                                    valueText={`${formatNum(depositData.tna, 2)}%`}
                                />
                            </GridTable.Data>
                            <GridTable.Data>
                                <CustomData
                                    titleText="deposits.autoRenewal"
                                    titleTextType="I18N"
                                    valueText={depositData.autoRenewalLabel}
                                />
                            </GridTable.Data>
                        </GridTable.Container>
                    </GridTable>
                    <GridTable>
                        <GridTable.Container>
                            <CredentialForm onSubmit={handleSubmit} credentialType={credentialType} />
                        </GridTable.Container>
                    </GridTable>
                </div>
            </MainContainer>
        </>
    );
};
DepositCancelRenewal.propTypes = {
    selectedDeposit: shape({}).isRequired,
    depositTicketId: string,
    depositTicketStatus: string,
    depositRequiredCredentials: arrayOf(string).isRequired,
    dispatch: func.isRequired,
    fetching: bool,
    depositsLength: number,
    isDesktop: bool.isRequired,
};

DepositCancelRenewal.defaultProps = {
    depositTicketId: "",
    depositTicketStatus: "",
    fetching: false,
    depositsLength: 0,
};

const mapStateToProps = (state) => ({
    selectedDeposit: depositsSelectors.getSelectedDeposit(state),
    depositRequiredCredentials: depositsSelectors.getDepositRequiredCredentials(state),
    depositTicketStatus: depositsSelectors.getDepositTicketStatus(state),
    depositTicketId: depositsSelectors.getDepositTicketId(state),
    fetching: depositsSelectors.getFetching(state),
    depositsLength: depositsSelectors.getDepositsLength(state),
});

export default connect(mapStateToProps)(DepositCancelRenewal);
